/*
 * Copyright (C) 2019 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import { AUDIO_PLAYER_SIZE as CANVAS_AUDIO_PLAYER_SIZE } from '@instructure/canvas-media';
import bridge from '../../../../bridge';
import { showFlashAlert } from '../../../../common/FlashAlert';
import formatMessage from '../../../../format-message';
import RCEGlobals from '../../../RCEGlobals';
import { asVideoElement } from '../../shared/ContentSelection';
import { findMediaPlayerIframe } from '../../shared/iframeUtils';
import { isStudioEmbeddedMedia, parseStudioOptions, updateStudioEmbedOptions, validateStudioEmbedOptions } from '../../shared/StudioLtiSupportUtils';
import VideoOptionsTray from '.';
export const CONTAINER_ID = 'instructure-video-options-tray-container';
export const STUDIO_PLAYER_VIDEO_SIZE_DEFAULT = {
  height: '300px',
  width: '480px'
};
export const AUDIO_PLAYER_SIZE = {
  width: `${CANVAS_AUDIO_PLAYER_SIZE.width}px`,
  height: `${CANVAS_AUDIO_PLAYER_SIZE.height}px`
};
export const videoDefaultSize = () => {
  return STUDIO_PLAYER_VIDEO_SIZE_DEFAULT;
};
function onStudioEmbedOptionChanged(editor, videoContainer) {
  return embedOptions => {
    if (validateStudioEmbedOptions(embedOptions)) {
      updateStudioEmbedOptions(editor, embedOptions, videoContainer);
    }
  };
}
export default class TrayController {
  constructor() {
    this._editor = null;
    this._isOpen = false;
    this._shouldOpen = false;
    this._renderId = 0;
    this._skipFocusOnExit = false;
    this._captionsModified = false;
    this.requestSubtitlesFromIframe = this.requestSubtitlesFromIframe.bind(this);
    this.isStudioVideo = false;
  }
  get $container() {
    let $container = document.getElementById(CONTAINER_ID);
    if ($container == null) {
      $container = document.createElement('div');
      $container.id = CONTAINER_ID;
      document.body.appendChild($container);
    }
    return $container;
  }
  get isOpen() {
    return this._isOpen;
  }
  showTrayForEditor(editor) {
    this._editor = editor;
    this.$videoContainer = findMediaPlayerIframe(editor.selection.getNode());
    this._shouldOpen = true;
    this._captionsModified = false;
    if (bridge.focusedEditor) {
      // Dismiss any content trays that may already be open
      bridge.hideTrays(); // Do we need to implement .hideTray functionality in this controller as well?
    }
    this.isStudioVideo = isStudioEmbeddedMedia(this.$videoContainer);
    // for studio embeds we don't need to show spinners
    // so it is ready by default
    this._isPlayerReady = this.isStudioVideo;

    // Clean broadcast listeners for any existing trays which are not shown (if not cleaned automatically)
    this._iframeLoadingListener?.abort();
    if (!this.isStudioVideo) {
      const videoOptions = asVideoElement(this.$videoContainer);
      this._listenForPlayerIframeToLoad(videoOptions.id);
    }
    this._renderId++;
    this._renderTray();
  }
  hideTrayForEditor(editor, skipFocusOnExit = false) {
    if (this._editor === editor) {
      this._skipFocusOnExit = skipFocusOnExit;
      this._dismissTray();
    }
  }
  _applyVideoOptions(videoOptions) {
    if (this.$videoContainer?.tagName === 'IFRAME') {
      const $tinymceIframeShim = this.$videoContainer.parentElement;
      if (videoOptions.displayAs === 'embed') {
        const isVertical = videoOptions.appliedHeight > videoOptions.appliedWidth;
        // player v5 requires more space for the CC button
        // TODO: remove when using v7
        const minWidth = videoOptions.subtitles?.length ? 400 : 320;
        const styl = {
          height: `${videoOptions.appliedHeight}px`,
          width: `${Math.max(minWidth, isVertical ? videoOptions.appliedHeight : videoOptions.appliedWidth)}px`
        };
        this._editor.dom.setStyles($tinymceIframeShim, styl);
        this._editor.dom.setStyles(this.$videoContainer, styl);
        const title = videoOptions.titleText;
        this._editor.dom.setAttrib($tinymceIframeShim, 'data-mce-p-title', title);
        this._editor.dom.setAttrib($tinymceIframeShim, 'data-mce-p-data-titleText', videoOptions.titleText);
        this._editor.dom.setAttrib(this.$videoContainer, 'title', title);
        this._editor.dom.setAttrib(this.$videoContainer, 'data-titleText', videoOptions.titleText);

        // tell tinymce so the context toolbar resets
        this._editor.fire('ObjectResized', {
          target: this.$videoContainer,
          width: videoOptions.appliedWidth,
          height: videoOptions.appliedHeight
        });
      } else {
        const href = this._editor.dom.getAttrib($tinymceIframeShim, 'data-mce-p-src');
        const title = videoOptions.titleText || this._editor.dom.getAttrib(this.$videoContainer, 'title');
        const link = document.createElement('a');
        link.setAttribute('href', href);
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noreferrer noopener');
        link.textContent = title;
        this._editor.dom.replace(link, $tinymceIframeShim);
        this._editor.selection.select(link);
        this.$videoContainer = null;
      }
      const isCaptionImprovements = RCEGlobals.getFeatures()?.rce_asr_captioning_improvements || false;
      const data = {
        media_object_id: videoOptions.media_object_id,
        title: videoOptions.titleText,
        attachment_id: videoOptions.attachment_id,
        subtitles: videoOptions.subtitles,
        skipCaptionUpdate: isCaptionImprovements,
        viewerRestrictions: videoOptions.viewerRestrictions
      };

      // If the video just edited came from a file uploaded to canvas
      // and not notorious, we probably don't have a media_object_id.
      // If not, we can't update the MediaObject in the canvas db.
      const hasMediaId = videoOptions.media_object_id && videoOptions.media_object_id !== 'undefined' || data.attachment_id && data.attachment_id !== 'undefined';
      if (hasMediaId && !videoOptions.editLocked) {
        videoOptions.updateMediaObject(data).then(_r => {
          if (this.$videoContainer && videoOptions.displayAs === 'embed') {
            if (isCaptionImprovements) {
              this._reloadVideoPlayer();
            } else {
              this.$videoContainer.contentWindow.postMessage({
                subject: 'reload_media',
                media_object_id: videoOptions.media_object_id,
                attachment_id: data.attachment_id
              }, bridge.canvasOrigin);
            }
          }
        }).catch(ex => {
          console.error('failed updating video captions', ex);
        });
      }
    }
    this._dismissTray();
    setTimeout(() => {
      showFlashAlert({
        message: formatMessage('Media options saved.'),
        type: 'success'
      });
    }, 0);
  }
  _listenForPlayerIframeToLoad(currentMediaId) {
    if (!bridge.canvasOrigin) return;
    this._iframeLoadingListener = new AbortController();

    // Wait for player iframe to be loaded
    window.addEventListener('message', event => {
      // If tray was opened before player iframe was ready it will catch ready event.
      // If not it will request it later and catch it here anyway.
      if (event.data?.subject === 'media_player.iframe_ready' && event.data?.mediaId === currentMediaId) {
        this._iframeLoadingListener.abort();
        this._isPlayerReady = true;
        this._renderTray();
      }
    }, {
      signal: this._iframeLoadingListener.signal
    });

    // If tray was opened after player was loaded we need to request iframe_ready state
    this.$videoContainer?.contentWindow?.postMessage({
      subject: 'media_player.get_ready_state'
    }, bridge.canvasOrigin);
  }
  _reloadVideoPlayer() {
    if (this.$videoContainer?.contentWindow?.location) {
      this.$videoContainer.contentWindow.location.reload();
    }
  }
  _dismissTray() {
    const isCaptionImprovements = RCEGlobals.getFeatures()?.rce_asr_captioning_improvements || false;

    // Reload if captions were modified AND feature flag enabled
    if (isCaptionImprovements && this._captionsModified && this.$videoContainer) {
      this._reloadVideoPlayer();
    }
    if (this.$videoContainer && !this._skipFocusOnExit) {
      this._editor?.selection?.select(this.$videoContainer);
    }
    this._shouldOpen = false;
    this._isOpen = false;
    this._renderTray();
    this._editor = null;
    this._iframeLoadingListener?.abort();
  }
  requestSubtitlesFromIframe(cb) {
    if (!bridge.canvasOrigin) return;
    this._subtitleListener = new AbortController();
    window.addEventListener('message', event => {
      if (event?.data?.subject === 'media_tracks_response') {
        cb(event?.data?.payload);
      }
    }, {
      signal: this._subtitleListener.signal
    });
    this.$videoContainer?.contentWindow?.postMessage({
      subject: 'media_tracks_request'
    }, bridge.canvasOrigin);
  }
  _renderTray() {
    const vo = asVideoElement(this.$videoContainer, this.isStudioVideo) || {};
    const element = /*#__PURE__*/React.createElement(VideoOptionsTray, {
      id: "video-options-tray",
      key: this._renderId,
      videoOptions: vo,
      onEntered: () => {
        this._isOpen = true;
      }
      // is not guaranteed to be called in case of showing another tray
      ,
      onExited: () => {
        if (!this._skipFocusOnExit) {
          bridge.focusActiveEditor(false);
        }
        this._skipFocusOnExit = false;
        this._isOpen = false;
        this._subtitleListener?.abort();
        this._iframeLoadingListener?.abort();
        this._isPlayerReady = false;
      },
      onSave: videoOptions => {
        this._applyVideoOptions(videoOptions);
      },
      onRequestClose: () => this._dismissTray(),
      onCaptionsModified: () => {
        this._captionsModified = true;
      },
      open: this._shouldOpen,
      trayProps: bridge.trayProps.get(this._editor),
      studioOptions: this.isStudioVideo ? parseStudioOptions(this.$videoContainer) : null,
      requestSubtitlesFromIframe: this.requestSubtitlesFromIframe,
      onStudioEmbedOptionChanged: onStudioEmbedOptionChanged(this._editor, this.$videoContainer),
      isLoading: !this._isPlayerReady
    });
    ReactDOM.render(element, this.$container);
  }
}